/**
 * When a refresh or a mutation is worth one more try (spec 036 in backend-senne, smoke S-02/S-08, 2026-09-24).
 *
 * Two tabs, or this portal and portal-senneliquor, share one API cookie jar. Each keeps its own copy of the CSRF
 * token in memory, and every `/auth/csrf` or `/auth/refresh` rewrites the cookie. So the other one's next call
 * answers 403 "CSRF token inválido ou ausente", which is a stale copy, not an ended session. And when both refresh
 * at once, the backend answers the loser 409 (`REFRESH_IN_PROGRESS`) instead of logging everyone out.
 * Either case is retried, at most three times: a 409 waits 400, 800 then 1600 ms for the winner's Set-Cookie to land
 * (review F6: one fixed 400 ms could fire before it), and every retry refetches the CSRF copy first. When the budget
 * runs out, the failure falls through to the usual expiry handling.
 */
type ErrorLike = {
  response?: { status?: number; data?: { message?: unknown; code?: unknown } };
};

export const RETRY_DELAYS_MS = [400, 800, 1600] as const;

/**
 * Whether a failed refresh ends the session. Only the server refusing it does: a 401, or a 403 that is not a stale
 * CSRF copy. A timeout, a network error, a 5xx or a 409 left after the retries keep the session, so the next call
 * tries again from the cookie (2026-10-28; same rule as portal-senneliquor `retryPolicy.ts`).
 */
export function refreshEndsSession(error: unknown): boolean {
  const status = (error as ErrorLike)?.response?.status;
  return status === 401 || (status === 403 && !isCsrfRejection(error));
}

export function isCsrfRejection(error: unknown): boolean {
  const res = (error as ErrorLike)?.response;
  return (
    res?.status === 403 &&
    typeof res.data?.message === "string" &&
    /csrf token/i.test(res.data.message)
  );
}

export function isRefreshInProgress(error: unknown): boolean {
  return (error as ErrorLike)?.response?.status === 409;
}

export interface RetryDeps {
  refetchCsrf: () => Promise<unknown>;
  wait: (ms: number) => Promise<void>;
}

/** Runs `attempt`; on a stale CSRF copy or a refresh in progress, refreshes the CSRF copy and tries again. */
export async function withSessionRetry<T>(
  attempt: () => Promise<T>,
  deps: RetryDeps,
  retriesDone = 0,
): Promise<T> {
  try {
    return await attempt();
  } catch (error) {
    const inProgress = isRefreshInProgress(error);
    if (
      retriesDone >= RETRY_DELAYS_MS.length ||
      (!inProgress && !isCsrfRejection(error))
    ) {
      throw error;
    }
    if (inProgress) {
      await deps.wait(RETRY_DELAYS_MS[retriesDone] ?? RETRY_DELAYS_MS[0]);
    }
    await deps.refetchCsrf();
    return withSessionRetry(attempt, deps, retriesDone + 1);
  }
}

export const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));
